import { Box } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { clsx } from '@mantine/styles'
import { useMediaQuery } from '@mantine/hooks'

import { Button, Feedback, Icon, Select } from '@percent/lemonade'
import { useSharedValidationRules } from '@percent/workplace-giving/common/hooks/useSharedValidationRules/useSharedValidationRules'
import { useOrgSearchCountries } from '@percent/workplace-giving/common/hooks/useOrgSearchCountries/useOrgSearchCountries'
import { OrgSearchFor } from '@percent/workplace-giving/api/search/searchOrganisations/searchVerifiedOrganisations.types'
import { ORGANISATION_SEARCH_MAX_LENGTH } from '@percent/workplace-giving/api/search/searchOrganisations/searchOrganisations.types'
import classes from './SearchBar.module.scss'
import { SINGLE_LINE_THRESHOLD_PX } from './searchBar.constants'
import { useCountryOptions } from '@percent/workplace-giving/common/hooks'
import { useAnimatedPlaceholder } from '@percent/workplace-giving/common/hooks/useAnimatedPlaceholder/useAnimatedPlaceholder'

export type SearchBarProps = Readonly<{
  query?: string
  onEmptyQuery?: () => void
  onQueryChange?: (query: string) => void
  countryCode: string
  onCountryChange?: (countryCode: string) => void
  onSearch?: (search: { country: string; query: string }) => void
  scrolled?: boolean
  size?: 'small' | 'medium'
  placeholder?: string
  allowEmptyQuery?: boolean
  showAiButton?: boolean
  onAiClick?: (query: string) => void
  hideCountrySelector?: boolean
  stackedOnMobile?: boolean
  disableMultiline?: boolean
  searchFor?: OrgSearchFor
}>

export function SearchBar({
  query,
  onEmptyQuery,
  onQueryChange,
  countryCode,
  onCountryChange,
  onSearch,
  size = 'small',
  placeholder,
  allowEmptyQuery,
  showAiButton,
  onAiClick,
  hideCountrySelector,
  stackedOnMobile,
  disableMultiline,
  searchFor = OrgSearchFor.DonationOrVolunteering
}: SearchBarProps) {
  const { t } = useTranslation()
  const { validateString } = useSharedValidationRules()
  const isMobile = useMediaQuery('(max-width: 759px)')
  const { allowedCountryCodes } = useOrgSearchCountries(searchFor)

  const alpha3CountryCodes = useCountryOptions({
    countryCodes: allowedCountryCodes,
    includeWorldOption: true
  })

  const countryData = useMemo((): typeof alpha3CountryCodes[number] => {
    const countryOption = alpha3CountryCodes.find(a => a.value === countryCode) || alpha3CountryCodes[0]

    if (countryOption) {
      return countryOption
    }

    return alpha3CountryCodes[0]
  }, [alpha3CountryCodes, countryCode])

  const { errors, values, setFieldValue, handleChange, submitForm } = useFormik({
    initialValues: {
      query: query ?? ''
    },
    validateOnChange: true,
    validationSchema: () =>
      Yup.object().shape({
        query: validateString({ optional: true, maxLength: ORGANISATION_SEARCH_MAX_LENGTH })
      }),
    onSubmit: ({ query: _query }) => {
      const trimmed = _query?.trim()

      if (trimmed !== '') {
        if (onQueryChange) {
          onQueryChange(trimmed)
        }

        if (onSearch) {
          onSearch({ country: countryCode, query: trimmed })
        }
      }

      if (trimmed === '' && allowEmptyQuery) {
        if (onQueryChange) {
          onQueryChange(trimmed)
        }

        if (onSearch) {
          onSearch({ country: countryCode, query: trimmed })
        }
      }
    }
  })

  useEffect(() => {
    if (!query || query === '') {
      setFieldValue('query', '')

      if (onEmptyQuery) {
        onEmptyQuery()
      }
    }
  }, [query, onEmptyQuery, setFieldValue])

  useEffect(() => {
    if (query && query !== values.query) {
      setFieldValue('query', query)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const {
    animatedPlaceholder,
    isAnimating,
    isFocused,
    onFocus: onAnimationFocus,
    onBlur: onAnimationBlur
  } = useAnimatedPlaceholder(values.query, !placeholder && !!showAiButton)
  const showButtons = hideCountrySelector || isFocused || !!values.query.trim()
  const isStacked = !!stackedOnMobile && !!isMobile

  const [doAnimation, setDoAnimation] = useState(true)

  useEffect(() => {
    if (!doAnimation) {
      return undefined
    }

    const timeout = setTimeout(() => {
      setDoAnimation(false)
    }, 1300)

    return () => clearTimeout(timeout)
  }, [doAnimation])

  const [isShaking, setIsShaking] = useState(false)
  const [isMultiline, setIsMultiline] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const singleLineHeightRef = useRef<number>(0)
  const shakeTimerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    return () => clearTimeout(shakeTimerRef.current)
  }, [])

  useEffect(() => {
    const el = textareaRef.current

    if (!el) return

    el.style.height = 'auto'
    const scrollH = el.scrollHeight
    const multi = !disableMultiline && (values.query.includes('\n') || scrollH > SINGLE_LINE_THRESHOLD_PX)

    if (!multi && wrapperRef.current) {
      singleLineHeightRef.current = wrapperRef.current.offsetHeight
    }

    el.style.height = `${scrollH}px`
    setIsMultiline(multi)
  }, [values.query, disableMultiline])

  const shakeInput = () => {
    textareaRef.current?.focus()
    setIsShaking(false)
    requestAnimationFrame(() => setIsShaking(true))
    shakeTimerRef.current = setTimeout(() => setIsShaking(false), 300)
  }

  const handleSearchSubmit = () => {
    if (!values.query.trim() && !allowEmptyQuery) {
      shakeInput()

      return
    }
    submitForm()
  }

  const handleAiClick = () => {
    const trimmed = values.query.trim()

    if (!trimmed) {
      shakeInput()

      return
    }
    onAiClick?.(trimmed)
  }

  const clearValue = () => {
    setFieldValue('query', '')

    if (allowEmptyQuery) {
      submitForm()
    }
  }

  const aiButton = showAiButton ? (
    <div
      className={clsx(classes.AiBorderWrapper, { [classes.AiBorderAnimate]: doAnimation })}
      onMouseEnter={() => {
        setDoAnimation(false)
        requestAnimationFrame(() => requestAnimationFrame(() => setDoAnimation(true)))
      }}
    >
      <Button
        onClick={handleAiClick}
        variant="secondary"
        shape="oval"
        size={size === 'small' ? 'small' : 'medium'}
        startIcon="reward-stars"
        startIconSize={4}
        data-testid="search-bar-ai-btn"
      >
        {t('workplace_giving.common.askAi')}
      </Button>
    </div>
  ) : null

  const searchButton = (
    <button
      onClick={handleSearchSubmit}
      className={clsx(classes.SearchBtn, { [classes.SearchBtnSmall]: size === 'small' })}
      type="button"
      data-testid="search-bar-btn"
      aria-label={t('workplace_giving.common.search')}
    >
      <Icon name="search" size={size === 'small' ? 5 : 6} color="white" />
    </button>
  )

  return (
    <div
      ref={wrapperRef}
      className={classes.WrapperPlaceholder}
      style={isMultiline ? { height: singleLineHeightRef.current } : undefined}
    >
      <Box
        className={clsx(classes.Wrapper, {
          [classes.WrapperShaking]: isShaking,
          [classes.WrapperMultiline]: isMultiline,
          [classes.WrapperStacked]: isStacked
        })}
      >
        <Box className={clsx(classes.Input, { [classes.InputMultiline]: isMultiline || isStacked })}>
          <textarea
            ref={textareaRef}
            name="query"
            className={classes.Textarea}
            value={values.query}
            onChange={handleChange}
            onFocus={onAnimationFocus}
            onBlur={onAnimationBlur}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSearchSubmit()
              }
            }}
            placeholder={
              placeholder || (isAnimating ? animatedPlaceholder : t('workplace_giving.home.globalSearchPlaceholder'))
            }
            aria-label={t('workplace_giving.common.search')}
            rows={1}
          />
          {!isMultiline && values.query && (
            <button
              type="button"
              className={classes.ClearBtn}
              onClick={clearValue}
              data-testid="clear-button"
              aria-label={t('workplace_giving.common.clearInput')}
            >
              <Icon name="reject" size={4} />
            </button>
          )}
        </Box>

        {isMultiline || isStacked ? (
          <Box className={classes.BottomActions}>
            {aiButton}
            {searchButton}
          </Box>
        ) : hideCountrySelector ? (
          <Box className={clsx(classes.ButtonsWrapper, { [classes.ButtonsWrapperVisible]: showButtons })}>
            {aiButton}
            {searchButton}
          </Box>
        ) : (
          <Box className={classes.SelectWrapper}>
            <Select
              placeholder={t('workplace_giving.common.selectCountry')}
              onChange={event => {
                if (onCountryChange) {
                  onCountryChange(event.value)
                }

                if (onSearch) {
                  onSearch({ country: event.value, query: values.query })
                }
              }}
              data-testid="country"
              defaultValue={countryData}
              options={alpha3CountryCodes}
              showPrefixForSelectedOption
              size={isMobile ? 'small' : size}
              searchable={!isMobile}
              displayPrefixOnly={isMobile}
            />
            {aiButton}
            {searchButton}
          </Box>
        )}
      </Box>
      {errors.query && <Feedback variant="critical">{errors.query}</Feedback>}
    </div>
  )
}
